import { useState } from 'react'
import { View, Text, Pressable, Modal, ScrollView, StyleSheet } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useTranslation } from 'react-i18next'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import { fonts } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { tierColor } from '../../lib/badges'
import GradientButton from '../GradientButton'
import BadgeMedal from './BadgeMedal'
import { useBadgeText } from './BadgeCard'

// Scegli fino a 3 badge ottenuti da mettere in vetrina (Home e album). "Automatica" torna ai piu' rari.
export default function ShowcaseEditSheet({ families, initial, onClose, onSave }) {
  const tc = useThemeColors()
  const { t } = useTranslation()
  const txt = useBadgeText()
  const insets = useSafeAreaInsets()
  const [sel, setSel] = useState(initial ?? [])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(false)

  const options = families.flatMap(f => f.levels.filter(l => l.earned).map(l => ({ f, l })))
    .sort((a, b) => (a.l.nEarned - b.l.nEarned) || (b.l.level - a.l.level))

  const toggle = (code) => setSel(cur => cur.includes(code) ? cur.filter(c => c !== code) : cur.length >= 3 ? cur : [...cur, code])
  const save = async (codes) => {
    setBusy(true); setError(false)
    const ok = await onSave(codes)
    setBusy(false)
    if (ok) onClose(); else setError(true)
  }

  return (
    <Modal visible transparent animationType="slide" onRequestClose={onClose}>
      <View style={s.overlay}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} accessibilityRole="button" accessibilityLabel={t('pick.close')} />
        <View style={[s.sheet, { backgroundColor: tc.bgCard, paddingBottom: insets.bottom + 12 }]}>
          <View style={[s.header, { borderBottomColor: tc.border }]}>
            <View style={{ flex: 1 }}>
              <Text style={[s.title, { color: tc.text }]} accessibilityRole="header">{t('badges.showcase_title')}</Text>
              <Text style={[s.hint, { color: tc.textMuted }]}>{t('badges.showcase_hint')}</Text>
            </View>
            <Pressable onPress={onClose} hitSlop={6} style={[s.closeBtn, { backgroundColor: tc.bgCard2 }]} accessibilityRole="button" accessibilityLabel={t('pick.close')}>
              <MaterialCommunityIcons name="close" size={18} color={tc.text} />
            </Pressable>
          </View>
          <ScrollView contentContainerStyle={{ padding: 12 }}>
            {options.length === 0 && <Text style={[s.empty, { color: tc.textMuted }]}>{t('badges.showcase_pick_empty')}</Text>}
            {options.map(({ f, l }) => {
              const on = sel.includes(l.code)
              const color = tierColor(l.level, f.maxLevel)
              return (
                <Pressable
                  key={l.code}
                  onPress={() => toggle(l.code)}
                  accessibilityRole="checkbox"
                  accessibilityState={{ checked: on, disabled: !on && sel.length >= 3 }}
                  accessibilityLabel={txt.levelTitle(f, l)}
                  style={[s.row, { borderBottomColor: tc.border, backgroundColor: on ? tc.rosaSubtle : 'transparent' }]}
                >
                  <BadgeMedal icon={f.icon} color={color} size={42} />
                  <View style={{ flex: 1 }}>
                    <Text style={[s.name, { color: tc.text }]} numberOfLines={2}>{txt.levelTitle(f, l)}</Text>
                    <Text style={[s.sub, { color: tc.textMuted }]} numberOfLines={1}>
                      {l.nEarned > 0 ? t('badges.rarity', { pct: l.nMembers ? Math.round(l.nEarned / l.nMembers * 100) : 0, n: l.nEarned, m: l.nMembers }) : ''}
                    </Text>
                  </View>
                  <MaterialCommunityIcons name={on ? 'checkbox-marked' : 'checkbox-blank-outline'} size={24} color={on ? tc.rosaText : tc.textMuted} />
                </Pressable>
              )
            })}
          </ScrollView>
          <View style={[s.footer, { borderTopColor: tc.border }]}>
            {error && <Text style={[s.err, { color: tc.rosaText }]} accessibilityRole="alert">{t('badges.showcase_err')}</Text>}
            <Text style={[s.count, { color: tc.textMuted }]}>{t('badges.showcase_count', { n: sel.length })}</Text>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <Pressable onPress={() => save([])} disabled={busy} accessibilityRole="button" style={[s.autoBtn, { borderColor: tc.border, backgroundColor: tc.bgCard2, opacity: busy ? 0.5 : 1 }]}>
                <Text style={[s.autoText, { color: tc.textMuted }]}>{t('badges.showcase_auto')}</Text>
              </Pressable>
              <GradientButton onPress={() => save(sel)} loading={busy} style={{ flex: 1 }}>{t('badges.showcase_save')}</GradientButton>
            </View>
          </View>
        </View>
      </View>
    </Modal>
  )
}

const s = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'flex-end' },
  sheet: { borderTopLeftRadius: 20, borderTopRightRadius: 20, maxHeight: '88%' },
  header: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, padding: 16, borderBottomWidth: 0.5 },
  title: { fontFamily: fonts.condensedBold, fontSize: 20 },
  hint: { fontSize: 13, lineHeight: 18, marginTop: 4 },
  closeBtn: { width: 44, height: 44, borderRadius: 22, alignItems: 'center', justifyContent: 'center' },
  empty: { fontSize: 14, textAlign: 'center', padding: 24 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 8, paddingVertical: 6, minHeight: 60, borderBottomWidth: 0.5, borderRadius: 10 },
  name: { fontFamily: fonts.condensedBold, fontSize: 15 },
  sub: { fontSize: 12 },
  footer: { padding: 16, gap: 10, borderTopWidth: 0.5 },
  count: { fontSize: 13 },
  err: { fontSize: 13 },
  autoBtn: { minHeight: 48, paddingHorizontal: 16, borderWidth: 1, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  autoText: { fontFamily: fonts.condensedBold, fontSize: 14 },
})
